import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  AlertTriangle, TrendingUp, TrendingDown, ChevronDown, RotateCcw,
  SlidersHorizontal, Download, Settings2, Info, X as XIcon, Lightbulb,
  ArrowRight, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const MONTHS_FR = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
const fmtEur = v => (v==null||isNaN(v))?"—":(v>=0?"+":"")+Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")+" €";
const fmtEurAbs = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")+" €";
const fmtInt = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ");
const fmtNum = (v,d=2) => (v==null||isNaN(v))?"—":Number(v).toLocaleString("fr-FR",{maximumFractionDigits:d,minimumFractionDigits:d});
const fmtPct = (v,d=1) => (v==null||isNaN(v))?"—":`${(v*100).toFixed(d).replace(".",",")}%`;
const fmtPctSigned = (v,d=1) => v==null?"—":`${v>=0?"+":""}${(v*100).toFixed(d).replace(".",",")}%`;

const SIGNAL_STYLES = {
  ecart_negatif_critique: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  ecart_positif_critique: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  anomalie_nouvelle: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  anomalie_recurrente: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  degradation_forte: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-500" },
  rupture_de_stock: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300", dot: "bg-rose-600" },
  ecart_positif: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  ecart_a_surveiller: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", dot: "bg-slate-400" },
};

const LEVEL_STYLES = {
  critique: { color: "#EF4444", label: "Critique" },
  important: { color: "#F59E0B", label: "Important" },
  surveillance: { color: "#16A34A", label: "Surveillance" },
  information: { color: "#2563EB", label: "Information" },
};

/* ─── Multi-select ─── */
function MS({ label, options, value, onChange, testid, labelMap, search=true }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labelMap} value={value||[]} onChange={onChange} testid={testid} size="md" topLabel className="flex-1 min-w-[140px]" placeholder={`Tous les ${label.toLowerCase()}`}/>;
}

/* ─── Sparkline SVG minimaliste ─── */
function Sparkline({ data, color="#2563eb", width=100, height=28, showArea=false }) {
  if (!data || data.length < 2) return <div className="text-[10px] text-slate-300 italic">—</div>;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v,i)=>`${(i*step).toFixed(1)},${(height - ((v-min)/range)*height).toFixed(1)}`).join(" ");
  const zeroY = min <= 0 && max >= 0 ? height - ((0-min)/range)*height : null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block">
      {zeroY !== null && <line x1={0} y1={zeroY} x2={width} y2={zeroY} stroke="#e2e8f0" strokeDasharray="2 2" strokeWidth={0.5}/>}
      {showArea && <polyline points={`0,${height} ${pts} ${width},${height}`} fill={color} fillOpacity={0.1} stroke="none"/>}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ─── Signal badge ─── */
function SignalBadge({ signal, label }) {
  if (!signal || !label) return <span className="text-slate-300 text-[11px]">—</span>;
  const s = SIGNAL_STYLES[signal] || SIGNAL_STYLES.ecart_a_surveiller;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/>
      {label}
    </span>
  );
}

/* ─── Drawer détail produits d'un corner ─── */
function CornerProductsDrawer({ corner_key, filters, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!corner_key) return;
    const p = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k,v]) => { if (Array.isArray(v) && v.length) p.append(k, v.join(",")); });
    p.append("corner_key", corner_key);
    api.get(`/kpi/inventaire/ecart/corner-products?${p.toString()}`)
       .then(r => setData(r.data))
       .catch(()=>setData({rows:[], total:{}, corner:null}));
  }, [corner_key, filters]);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose}/>
      <div className="fixed right-0 top-0 h-full w-[760px] max-w-full bg-white z-50 shadow-2xl overflow-y-auto p-5" data-testid="ecart-drawer">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Détail écarts produits</div>
            <div className="text-lg font-bold text-slate-900">{data?.corner?.corner || corner_key}</div>
            <div className="text-[11px] text-slate-500">
              {data?.corner?.dr && `DR ${data.corner.dr}`}
              {data?.corner?.secteur && ` · ${data.corner.secteur}`}
              {data?.corner?.enseigne && ` · ${data.corner.enseigne}`}
            </div>
          </div>
          <button onClick={onClose} data-testid="ecart-drawer-close" className="p-1 rounded hover:bg-slate-100"><XIcon className="w-5 h-5"/></button>
        </div>
        {!data && <div className="text-sm text-slate-400 italic">Chargement…</div>}
        {data && data.rows && data.rows.length === 0 && <div className="text-sm text-slate-400 italic py-6 text-center">Aucun produit inventorié pour ce corner avec ces filtres.</div>}
        {data && data.rows && data.rows.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              {data.rows.length} produits · classés par écart € croissant (plus grosse perte en tête)
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-[720px] w-full text-[12px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Produit</th>
                    <th className="px-3 py-2 text-right">Stock théo. Qté</th>
                    <th className="px-3 py-2 text-right">Stock compté Qté</th>
                    <th className="px-3 py-2 text-right">Écart Qté</th>
                    <th className="px-3 py-2 text-right">Prix U.</th>
                    <th className="px-3 py-2 text-right">Écart €</th>
                    <th className="px-3 py-2 text-right">Écart %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const pct = r.ecart_pct || 0;
                    const cls = Math.abs(pct) > 0.05 ? "bg-rose-50 text-rose-700" : Math.abs(pct) > 0.02 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
                    const negTone = (r.ecart_eur || 0) < 0 ? "text-rose-600" : (r.ecart_eur||0) > 0 ? "text-emerald-600" : "text-slate-500";
                    return (
                      <tr key={i} data-testid={`ecart-drawer-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-semibold text-slate-900">{r.produit}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_theorique_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_compte_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtNum(r.ecart_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.prix_unitaire==null?"—":fmtEurAbs(r.prix_unitaire)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${negTone}`}>{fmtEur(r.ecart_eur)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold rounded ${cls}`}>{fmtPctSigned(pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr className="text-[12px] font-bold text-slate-900">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.stock_theorique_qte||0,2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.stock_compte_qte||0,2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.ecart_qte||0,2)}</td>
                    <td className="px-3 py-2"></td>
                    <td className={`px-3 py-2 text-right tabular-nums ${(data.total?.ecart_eur||0)<0?"text-rose-600":"text-emerald-600"}`}>{fmtEur(data.total?.ecart_eur||0)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ScoreBar({ score }) {
  const color = score >= 80 ? "bg-rose-500" : score >= 60 ? "bg-amber-500" : score >= 40 ? "bg-blue-500" : "bg-slate-300";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{width:`${Math.min(100, Math.max(0, score))}%`}}/>
      </div>
      <span className="text-[11px] font-bold text-slate-700 tabular-nums w-6 text-right">{score}</span>
    </div>
  );
}

/* ─── Filtres avancés popover ─── */
function AdvancedFiltersPopover({ settings, onSave }) {
  const [open, setOpen] = useState(false);
  const [seuilEur, setSeuilEur] = useState(settings?.seuil_critique_eur || 100);
  const [seuilM1, setSeuilM1] = useState(settings?.seuil_nouvelle_pct || 9);
  useEffect(()=>{
    setSeuilEur(settings?.seuil_critique_eur || 100);
    setSeuilM1(settings?.seuil_nouvelle_pct || 9);
  }, [settings]);
  return (
    <div className="relative">
      <button data-testid="ecart-adv-filters" onClick={()=>setOpen(o=>!o)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:border-slate-300">
        <SlidersHorizontal className="w-3.5 h-3.5"/> Filtres avancés
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={()=>setOpen(false)}/>
          <div className="absolute right-0 mt-1 w-[320px] bg-white border border-slate-200 rounded-lg shadow-xl z-40 p-4">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Seuils de détection</h4>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-700">Écart critique en €</span>
                <input type="number" value={seuilEur} onChange={e=>setSeuilEur(Number(e.target.value)||0)}
                       className="mt-1 w-full px-2 py-1.5 rounded border border-slate-200 text-[13px] tabular-nums"/>
                <span className="text-[10px] text-slate-500">|écart| au-delà = signal critique</span>
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-700">Anomalie nouvelle (× M-1)</span>
                <input type="number" value={seuilM1} onChange={e=>setSeuilM1(Number(e.target.value)||0)}
                       className="mt-1 w-full px-2 py-1.5 rounded border border-slate-200 text-[13px] tabular-nums"/>
                <span className="text-[10px] text-slate-500">|écart| &gt; N × |M-1| = anomalie nouvelle</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setOpen(false)} className="text-[11px] font-semibold text-slate-500 px-3 py-1">Annuler</button>
              <button data-testid="ecart-adv-save" onClick={()=>{onSave({seuil_critique_eur:seuilEur, seuil_nouvelle_pct:seuilM1}); setOpen(false);}}
                      className="text-[11px] font-semibold px-3 py-1.5 bg-blue-600 text-white rounded">Appliquer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Barre de filtres ─── */
function EcartFilterBar({ meta, f, setF, settings, onSettingsChange, onExport }) {
  const drOpts = useMemo(()=>(meta?.drs||[]).slice().sort(),[meta]);
  const secteurOpts = useMemo(()=>(meta?.secteurs||[]).slice().sort(),[meta]);
  const enseigneOpts = useMemo(()=>(meta?.enseignes||[]).slice().sort(),[meta]);
  const corners = useMemo(()=>{
    const drs=f.drs||[], secteurs=f.secteurs||[], ens=f.enseignes||[]; const seen=new Set(); const keys=[], labels={};
    (meta?.corners||[]).forEach(c=>{
      if (drs.length && !drs.includes(c.dr)) return;
      if (secteurs.length && !secteurs.includes(c.secteur)) return;
      if (ens.length && !ens.includes(c.enseigne)) return;
      if (!c.corner_key || seen.has(c.corner_key)) return;
      seen.add(c.corner_key);
      keys.push(c.corner_key); labels[c.corner_key]=c.corner||c.corner_key;
    });
    keys.sort((a,b)=>(labels[a]||"").localeCompare(labels[b]||""));
    return { keys, labels };
  },[meta,f.drs,f.secteurs,f.enseignes]);
  const prodOpts = useMemo(()=>(meta?.produits||[]).slice().sort(),[meta]);
  const anOpts = useMemo(()=>(meta?.annees||[]).slice().sort((a,b)=>b-a).map(String),[meta]);
  const moOpts = useMemo(()=>(meta?.mois||[]).slice().sort((a,b)=>MONTHS_FR.indexOf(a)-MONTHS_FR.indexOf(b)),[meta]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid="ecart-filter-bar">
      <MS label="Enseigne" testid="ecart-f-enseigne" options={enseigneOpts} value={f.enseignes||[]} onChange={v=>setF(p=>({...p,enseignes:v}))}/>
      <MS label="DR"      testid="ecart-f-dr"      options={drOpts}      value={f.drs||[]}      onChange={v=>setF(p=>({...p,drs:v}))}/>
      <MS label="Secteur" testid="ecart-f-secteur" options={secteurOpts} value={f.secteurs||[]} onChange={v=>setF(p=>({...p,secteurs:v}))}/>
      <MS label="Corner"  testid="ecart-f-corner"  options={corners.keys} labelMap={corners.labels} value={f.corners||[]} onChange={v=>setF(p=>({...p,corners:v}))}/>
      <MS label="Produit" testid="ecart-f-produit" options={prodOpts}    value={f.produits||[]} onChange={v=>setF(p=>({...p,produits:v}))}/>
      <MS label="Année"   testid="ecart-f-annee"   options={anOpts}      value={(f.annees||[]).map(String)} onChange={v=>setF(p=>({...p,annees:v.map(Number)}))}/>
      <MS label="Mois"    testid="ecart-f-mois"    options={moOpts}      value={f.mois||[]}     onChange={v=>setF(p=>({...p,mois:v}))}/>
      <button onClick={()=>setF({drs:[],secteurs:[],enseignes:[],corners:[],produits:[],mois:[],annees:[]})}
              data-testid="ecart-f-reset"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:border-slate-300">
        <RotateCcw className="w-3.5 h-3.5"/> Réinitialiser
      </button>
      <AdvancedFiltersPopover settings={settings} onSave={onSettingsChange}/>
      <button data-testid="ecart-export" onClick={onExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700">
        <Download className="w-3.5 h-3.5"/> Exporter
      </button>
    </div>
  );
}

/* ─── Card synthèse ─── */
function StatCard({ icon: Icon, iconBg, label, value, sub, subTone, testid, alert }) {
  const toneClass = subTone === "up" ? "text-emerald-600" : subTone === "down" ? "text-rose-600" : "text-slate-500";
  return (
    <div data-testid={testid} className={`bg-white rounded-xl border ${alert?"border-rose-300":"border-slate-200"} p-4 flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${iconBg}`}>
          <Icon className="w-4 h-4 text-white"/>
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex-1">{label}</div>
        <Info className="w-3 h-3 text-slate-300"/>
      </div>
      <div className="text-[24px] font-bold text-slate-900 tabular-nums leading-tight">{value}</div>
      {sub && <div className={`text-[11px] font-semibold ${toneClass}`}>{sub}</div>}
    </div>
  );
}

/* ─── Donut Répartition ─── */
function DonutRepartition({ data, title, testid }) {
  const total = data.reduce((s,d)=>s + (d.count||0), 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-wider">{title}</h3>
        <Info className="w-3 h-3 text-slate-300"/>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-[180px] h-[180px] relative shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.map(d=>({name:d.label, value:Math.max(d.count||0, 0.0001), color:d.color}))}
                   dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={1}>
                {data.map((d,i)=><Cell key={i} fill={d.color}/>)}
              </Pie>
              <Tooltip formatter={(v,n)=>[fmtInt(v), n]}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          {data.map((d,i)=>(
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:d.color}}/>
              <span className="flex-1 truncate text-slate-700">{d.label}</span>
              <span className="tabular-nums text-slate-900 font-semibold">
                {d.value !== undefined ? fmtEur(d.value) : fmtInt(d.count)}
              </span>
              <span className="tabular-nums text-slate-500 w-10 text-right">
                ({fmtPct(d.pct, 0)})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Timeseries Chart ─── */
function TimeseriesChart({ data, testid }) {
  const [mode, setMode] = useState("eur");
  const chartData = (data||[]).map(d => ({
    label: d.label,
    value: mode === "eur" ? d.ecart_eur : d.ecart_qte,
  }));
  const last = chartData[chartData.length-1];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-wider">Évolution des écarts – 12 derniers mois</h3>
        <div className="ml-auto flex items-center border border-slate-200 rounded overflow-hidden">
          <button onClick={()=>setMode("eur")} data-testid="ecart-ts-eur"
                  className={`px-2 py-1 text-[11px] font-semibold ${mode==="eur"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>€ (montant)</button>
          <button onClick={()=>setMode("qte")} data-testid="ecart-ts-qte"
                  className={`px-2 py-1 text-[11px] font-semibold ${mode==="qte"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>Unités</button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={chartData} margin={{top:10,right:20,left:0,bottom:0}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
          <XAxis dataKey="label" tick={{fontSize:10}}/>
          <YAxis tick={{fontSize:10}} width={70}
                 tickFormatter={v => mode==="eur" ? `${Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")} €` : fmtInt(v)}/>
          <Tooltip formatter={v => mode==="eur" ? fmtEur(v) : fmtInt(v)}/>
          <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={{r:3, fill:"#ef4444"}}
                activeDot={{r:5}} isAnimationActive={false}/>
        </LineChart>
      </ResponsiveContainer>
      {last && (
        <div className="text-right text-[12px] font-bold text-rose-600 -mt-4 mr-2">
          {mode==="eur" ? fmtEur(last.value) : fmtInt(last.value)}
        </div>
      )}
    </div>
  );
}

/* ─── Top 5 ─── */
function TopFiveBars({ items, mode, sign, testid }) {
  if (!items || items.length === 0) return <div className="text-[11px] italic text-slate-400 py-4 text-center">Aucune donnée</div>;
  const values = items.map(i => mode==="eur" ? Math.abs(i.ecart_eur) : Math.abs(i.ecart_qte));
  const max = Math.max(...values, 1);
  const barColor = sign === "neg" ? "#ef4444" : "#10b981";
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => {
        const val = mode==="eur" ? it.ecart_eur : it.ecart_qte;
        const w = Math.max(4, (Math.abs(val) / max) * 100);
        return (
          <div key={i} className="flex items-center gap-2 text-[11px]">
            <span className="flex-1 min-w-0 truncate font-semibold text-slate-800">{it.produit || "—"}</span>
            <div className="w-[100px] h-4 bg-slate-50 rounded overflow-hidden">
              <div className="h-full rounded" style={{width:`${w}%`, background:barColor}}/>
            </div>
            <span className={`tabular-nums font-bold w-[80px] text-right ${sign==="neg"?"text-rose-600":"text-emerald-600"}`}>
              {mode==="eur" ? fmtEur(val) : fmtInt(val)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TopFivePanel({ negatifs, positifs }) {
  const [mode, setMode] = useState("eur");
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-wider flex-1">Top 5 écarts négatifs</h3>
          <div className="flex items-center border border-slate-200 rounded overflow-hidden">
            <button onClick={()=>setMode("eur")} className={`px-2 py-1 text-[10px] font-semibold ${mode==="eur"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>Montant</button>
            <button onClick={()=>setMode("qte")} className={`px-2 py-1 text-[10px] font-semibold ${mode==="qte"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>Unités</button>
          </div>
        </div>
        <TopFiveBars items={negatifs} mode={mode} sign="neg" testid="ecart-top5-neg"/>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-wider flex-1">Top 5 écarts positifs</h3>
          <div className="flex items-center border border-slate-200 rounded overflow-hidden">
            <button onClick={()=>setMode("eur")} className={`px-2 py-1 text-[10px] font-semibold ${mode==="eur"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>Montant</button>
            <button onClick={()=>setMode("qte")} className={`px-2 py-1 text-[10px] font-semibold ${mode==="qte"?"bg-blue-600 text-white":"bg-white text-slate-600"}`}>Unités</button>
          </div>
        </div>
        <TopFiveBars items={positifs} mode={mode} sign="pos" testid="ecart-top5-pos"/>
      </div>
    </div>
  );
}

/* ─── Bloc Signaux ─── */
function SignalMiniCard({ icon:Icon, iconColor, label, sublabel, count, sparkline, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded grid place-items-center" style={{background:`${iconColor}15`}}>
          <Icon className="w-3.5 h-3.5" style={{color:iconColor}}/>
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex-1 truncate">{label}</div>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-tight">{fmtInt(count)}</div>
          <div className="text-[10px] text-slate-500 truncate">{sublabel}</div>
        </div>
        <Sparkline data={sparkline} color={iconColor} width={70} height={26} showArea/>
      </div>
    </div>
  );
}

function SignauxBlock({ signaux, distribution }) {
  if (!signaux) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-slate-500"/>
        <h3 className="text-[13px] font-bold text-slate-800 uppercase tracking-wider">Signaux d'anomalies détectés</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
        <SignalMiniCard testid="sig-card-neg" icon={AlertTriangle} iconColor="#EF4444"
                        label="Écarts négatifs critiques" sublabel={signaux.ecart_negatif_critique.sublabel}
                        count={signaux.ecart_negatif_critique.count} sparkline={signaux.ecart_negatif_critique.sparkline}/>
        <SignalMiniCard testid="sig-card-pos" icon={AlertTriangle} iconColor="#16A34A"
                        label="Écarts positifs critiques" sublabel={signaux.ecart_positif_critique.sublabel}
                        count={signaux.ecart_positif_critique.count} sparkline={signaux.ecart_positif_critique.sparkline}/>
        <SignalMiniCard testid="sig-card-nouv" icon={AlertTriangle} iconColor="#F59E0B"
                        label="Anomalies nouvelles" sublabel={signaux.anomalie_nouvelle.sublabel}
                        count={signaux.anomalie_nouvelle.count} sparkline={signaux.anomalie_nouvelle.sparkline}/>
        <SignalMiniCard testid="sig-card-rec" icon={AlertTriangle} iconColor="#2563EB"
                        label="Anomalies récurrentes" sublabel={signaux.anomalie_recurrente.sublabel}
                        count={signaux.anomalie_recurrente.count} sparkline={signaux.anomalie_recurrente.sparkline}/>
        <SignalMiniCard testid="sig-card-deg" icon={AlertTriangle} iconColor="#8B5CF6"
                        label="Dégradations fortes" sublabel={signaux.degradation_forte.sublabel}
                        count={signaux.degradation_forte.count} sparkline={signaux.degradation_forte.sparkline}/>
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Répartition des signaux</div>
          <div className="flex items-center gap-2">
            <div className="w-16 h-16 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={distribution.map(d=>({name:d.label, value:Math.max(d.count||0,0.0001), color:d.color}))}
                       dataKey="value" innerRadius={18} outerRadius={30} paddingAngle={1}>
                    {distribution.map((d,i)=><Cell key={i} fill={d.color}/>)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-0.5 min-w-0">
              {distribution.map((d,i)=>(
                <div key={i} className="flex items-center gap-1 text-[9px] min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:d.color}}/>
                  <span className="truncate text-slate-600">{d.label}</span>
                  <span className="tabular-nums font-semibold text-slate-900 ml-auto">{fmtPct(d.pct, 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-3 items-start">
        <Lightbulb className="w-4 h-4 text-blue-600 shrink-0 mt-0.5"/>
        <div className="text-[11px] text-slate-700">
          <div className="font-bold uppercase tracking-wider text-blue-700 mb-1">Comment lire les signaux ?</div>
          Nous détectons des écarts significatifs et des variations inhabituelles à partir : (1) du montant de l'écart (€),
          (2) de l'évolution vs les inventaires précédents, (3) de la récurrence des écarts sur plusieurs mois consécutifs.
          Un signal est déclenché lorsqu'un écart sort des seuils ou présente une évolution anormale.
        </div>
      </div>
    </div>
  );
}

/* ═══ COMPOSANT PRINCIPAL ═══ */
export default function TabEcartsPro() {
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ drs:[], secteurs:[], enseignes:[], corners:[], produits:[], mois:[], annees:[] });
  const [view, setView] = useState("references");
  const [settings, setSettings] = useState({ seuil_critique_eur: 100, seuil_nouvelle_pct: 9 });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("rang");
  const [sortDir, setSortDir] = useState("asc");
  const onSort = (k) => { if (sortBy === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(k); setSortDir(k === "produit" || k === "corner" || k === "rang" ? "asc" : "desc"); } };
  const pageSize = 50;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawerCk, setDrawerCk] = useState(null);

  useEffect(() => {
    api.get(`/kpi/inventaire/meta?coll=kpi_ecart`).then(r => setMeta(r.data)).catch(()=>setMeta({}));
  }, []);

  useEffect(() => { setPage(1); }, [f, view, settings, sortBy, sortDir]);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k,v]) => { if (Array.isArray(v) && v.length) p.append(k, v.join(",")); });
    p.append("view", view);
    p.append("seuil_critique_eur", String(settings.seuil_critique_eur));
    p.append("seuil_nouvelle_pct", String(settings.seuil_nouvelle_pct));
    p.append("sort_by", sortBy); p.append("sort_dir", sortDir);
    return p;
  }, [f, view, settings, sortBy, sortDir]);

  const reload = useCallback(() => {
    setLoading(true);
    const p = buildParams();
    p.append("page", String(page));
    p.append("page_size", String(pageSize));
    api.get(`/kpi/inventaire/ecart/dashboard?${p.toString()}`)
       .then(r => setData(r.data))
       .catch(err => { toast.error("Erreur chargement écarts"); setData(null); })
       .finally(()=>setLoading(false));
  }, [buildParams, page]);
  useEffect(() => { reload(); }, [reload]);

  const [exporting, setExporting] = useState(false);
  const doExportXlsx = async () => {
    setExporting(true);
    try {
      const p = buildParams(); p.append("export", "xlsx");
      const r = await api.get(`/kpi/inventaire/ecart/dashboard?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `ecarts_${view}_${data?.cards?.current_mois||""}_${data?.cards?.current_annee||""}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch { toast.error("Échec de l'export Excel"); }
    finally { setExporting(false); }
  };

  const doExport = () => {
    if (!data || !data.rows) { toast.error("Aucune donnée à exporter"); return; }
    const cols = ["Rang","Référence","Corner","DR","Secteur","Stock Théo Qté","Stock Réel Qté",
                   "Écart Qté","Écart €","Écart %","VS M-1 €","Signal","Score priorité"];
    const lines = [cols.join(";")];
    data.rows.forEach(r => lines.push([
      r.rang, r.produit, r.corner, r.dr||"", r.secteur||"",
      r.stock_theo_qte, r.stock_reel_qte, r.ecart_qte, r.ecart_eur,
      ((r.ecart_pct||0)*100).toFixed(2).replace(".",","),
      r.vs_m1_eur ?? "", r.signal_label||"", r.score_priorite
    ].join(";")));
    const blob = new Blob(["\ufeff"+lines.join("\n")], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `ecarts_${data.cards?.current_mois||""}_${data.cards?.current_annee||""}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Export téléchargé");
  };

  const cards = data?.cards || {};
  const totalPages = data?.pagination?.total_pages || 1;

  return (
    <div className="space-y-3" data-testid="ecart-pro-tab">
      <EcartFilterBar meta={meta} f={f} setF={setF}
                       settings={settings} onSettingsChange={setSettings}
                       onExport={doExport}/>

      {/* 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testid="ecart-card-total" icon={AlertTriangle} iconBg="bg-slate-800"
                  label="Écart Total (€)" value={fmtEur(cards.ecart_total)}
                  sub={cards.ecart_total_evol_pct != null ? `${fmtPctSigned(cards.ecart_total_evol_pct)} vs M-1` : "—"}
                  subTone={cards.ecart_total_evol_pct > 0 ? "down" : cards.ecart_total_evol_pct < 0 ? "up" : ""}/>
        <StatCard testid="ecart-card-pos" icon={TrendingUp} iconBg="bg-emerald-600"
                  label="Écart Positif (€)" value={fmtEur(cards.ecart_positif)}
                  sub={`${fmtPct(cards.ecart_positif_pct_refs, 1)} des références`} subTone=""/>
        <StatCard testid="ecart-card-neg" icon={TrendingDown} iconBg="bg-rose-600"
                  label="Écart Négatif (€)" value={fmtEur(cards.ecart_negatif)}
                  sub={`${fmtPct(cards.ecart_negatif_pct_refs, 1)} des références`} subTone="down"/>
        <StatCard testid="ecart-card-refs" icon={Info} iconBg="bg-blue-600"
                  label="Références en écart" value={fmtInt(cards.refs_en_ecart)}
                  sub={`sur ${fmtInt(cards.refs_total)} références`}/>
        <StatCard testid="ecart-card-moy" icon={TrendingDown} iconBg="bg-slate-600"
                  label="Écart Moyen / Réf." value={fmtEur(cards.ecart_moyen_ref)}
                  sub="par référence en écart"/>
        <StatCard testid="ecart-card-alerts" icon={AlertTriangle} iconBg="bg-rose-500"
                  label="Alertes Actives" value={fmtInt(cards.alertes_actives)}
                  sub="signaux détectés" alert={cards.alertes_actives > 0}/>
      </div>

      {/* Bloc central 3 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <DonutRepartition testid="ecart-donut" title="Répartition des écarts (€)" data={data?.distribution || []}/>
        <TimeseriesChart testid="ecart-ts" data={data?.timeseries || []}/>
        <TopFivePanel negatifs={data?.top5_negatifs || []} positifs={data?.top5_positifs || []}/>
      </div>

      {/* Bloc signaux */}
      <SignauxBlock signaux={data?.signaux} distribution={data?.signaux_distribution || []}/>

      {/* Tableau détaillé */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="ecart-view">
            {[
              {k:"references", label:"Vue Références"},
              {k:"corner", label:"Vue Corner"},
              {k:"secteur", label:"Vue Secteur"},
            ].map(o => (
              <button key={o.k} data-testid={`ecart-view-${o.k}`} onClick={()=>setView(o.k)}
                      className={`px-3 py-1 text-[11px] font-semibold transition ${view===o.k?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-slate-500 ml-2">
            {loading ? "Chargement…" : `${data?.pagination?.total_rows || 0} ${view === "references" ? "références" : view === "corner" ? "corners" : "secteurs"}`}
          </span>
          <button className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  data-testid="ecart-export-xlsx" disabled={exporting || !data?.rows?.length} onClick={doExportXlsx}>
            <Download className="w-3 h-3"/> {exporting ? "Export…" : "Exporter Excel"}
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-200 text-[11px] font-semibold text-slate-700 hover:border-slate-300"
                  data-testid="ecart-configure-thresholds"
                  onClick={()=>toast.info("Utilise le bouton 'Filtres avancés' en haut pour configurer les seuils")}>
            <Settings2 className="w-3 h-3"/> Configurer les seuils
          </button>
        </div>
        <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
          <table className="min-w-[1400px] w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                {[
                  { k: "rang", label: "Rang", cls: "w-[45px]" },
                  { k: "signal_level", label: "Alerte", cls: "w-[55px]" },
                  { k: view === "references" ? "produit" : "corner", label: view === "corner" ? "Corner" : view === "secteur" ? "Secteur" : "Référence", cls: "min-w-[220px]" },
                  { k: view === "references" ? "corner" : "dr", label: view === "references" ? "Corner" : view === "corner" ? "DR" : "—", cls: "min-w-[120px]", disabled: view === "secteur" },
                  { k: "stock_theo_qte", label: "Stock théo. (unité)", cls: "min-w-[110px]", right: true },
                  { k: "stock_reel_qte", label: "Stock réel (unité)", cls: "min-w-[130px]", right: true },
                  { k: "ecart_qte", label: "Écart (unité)", cls: "min-w-[100px]", right: true },
                  { k: "ecart_eur", label: "Écart €", cls: "min-w-[100px]", right: true },
                  { k: "ecart_pct", label: "Écart %", cls: "min-w-[80px]", right: true },
                  { k: "vs_m1_eur", label: "VS M-1 (€)", cls: "min-w-[100px]", right: true },
                  { k: null, label: "Tendance", cls: "min-w-[120px]" },
                  { k: "signal_label", label: "Signal", cls: "min-w-[160px]" },
                  { k: "score_priorite", label: "Score priorité", cls: "min-w-[110px]" },
                ].map((c, i) => (
                  <th key={i} className={`px-3 py-2 ${c.cls} ${c.right ? "text-right" : ""}`}>
                    {c.k && !c.disabled ? (
                      <button type="button" onClick={() => onSort(c.k)} data-testid={`ecart-sort-${c.k}`}
                              className={`inline-flex items-center gap-1 uppercase hover:text-slate-800 ${c.right ? "justify-end w-full" : ""} ${sortBy === c.k ? "text-blue-600" : ""}`}>
                        {c.label}
                        {sortBy === c.k ? (sortDir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>) : <ChevronsUpDown className="w-3 h-3 text-slate-300"/>}
                      </button>
                    ) : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, idx) => {
                const sparkColor = (r.ecart_eur||0) < 0 ? "#ef4444" : "#10b981";
                const clickable = view !== "secteur" && r.corner_key;
                return (
                  <tr key={`${r.corner_key}-${r.produit_key||idx}`} data-testid={`ecart-row-${idx}`}
                      onClick={()=>clickable && setDrawerCk(r.corner_key)}
                      className={`border-b border-slate-100 ${clickable?"hover:bg-blue-50/60 cursor-pointer":"hover:bg-slate-50/60"}`}>
                    <td className="px-3 py-2 font-bold text-slate-500 tabular-nums">{r.rang}</td>
                    <td className="px-3 py-2">
                      {r.signal_level === "critique" && <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                      {r.signal_level === "important" && <AlertTriangle className="w-4 h-4 text-amber-600"/>}
                      {r.signal_level === "surveillance" && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"/>}
                      {r.signal_level === "information" && <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/>}
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      {view === "references" ? r.produit : r.corner}
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate">
                      {view === "references" ? r.corner : view === "corner" ? (r.dr || "—") : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_theo_qte, 2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_reel_qte, 2)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.ecart_qte<0?"text-rose-600":r.ecart_qte>0?"text-emerald-600":"text-slate-500"}`}>{fmtNum(r.ecart_qte, 2)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.ecart_eur<0?"text-rose-600":r.ecart_eur>0?"text-emerald-600":"text-slate-500"}`}>{fmtEur(r.ecart_eur)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${Math.abs(r.ecart_pct||0)>0.5?"text-rose-600":Math.abs(r.ecart_pct||0)>0.05?"text-amber-600":"text-slate-500"}`}>{fmtPctSigned(r.ecart_pct)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.vs_m1_eur==null?"text-slate-400":r.vs_m1_eur<0?"text-rose-600":"text-emerald-600"}`}>{r.vs_m1_eur==null?"—":fmtEur(r.vs_m1_eur)}</td>
                    <td className="px-3 py-2"><Sparkline data={r.sparkline} color={sparkColor} width={100} height={24}/></td>
                    <td className="px-3 py-2"><SignalBadge signal={r.signal} label={r.signal_label}/></td>
                    <td className="px-3 py-2"><ScoreBar score={r.score_priorite||0}/></td>
                  </tr>
                );
              })}
              {(!data?.rows || data.rows.length === 0) && (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-400 italic">
                  {loading ? "Chargement…" : "Aucune donnée pour ces filtres."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center flex-wrap gap-3">
          {/* Légende */}
          <div className="flex items-center gap-4 text-[10px]">
            {Object.entries(LEVEL_STYLES).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{background:v.color}}/>
                <span className="text-slate-600">{v.label}</span>
              </div>
            ))}
          </div>
          {/* Pagination */}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-slate-500 tabular-nums">
              {data?.pagination ? `${((page-1)*pageSize)+1}-${Math.min(page*pageSize, data.pagination.total_rows)} sur ${fmtInt(data.pagination.total_rows)}` : ""}
            </span>
            <button data-testid="ecart-page-prev" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}
                    className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5"/></button>
            <span className="text-[11px] font-semibold text-slate-700 tabular-nums px-2">Page {page} / {totalPages}</span>
            <button data-testid="ecart-page-next" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}
                    className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5"/></button>
          </div>
        </div>
      </div>

      {drawerCk && <CornerProductsDrawer corner_key={drawerCk} filters={f} onClose={()=>setDrawerCk(null)}/>}
    </div>
  );
}
